import { PRICE_LOGIC } from '../../helper/constants';
import { ProductsApiService } from '../website/productsApiService';
import { ToastTypes, useMessage } from '../../base/context/MessageProvider';
import { PriceRange } from '../../types/api';
import { RecordNumber } from '../../types/common';
import { toast } from 'react-toastify';
import { Variation } from '../../types/product';
import { calculateFBP } from '../../website/component/views/productDetails/utils';

const Message = () => useMessage();

interface ShippingInfo {
  /** Product-level (parent) shipping — used when sku row weight/dimensions are 0 */
  weight?: number;
  length?: number;
  height?: number;
  width?: number;
  skuShippingDetails?: Array<{
    skuId: string | number;
    weight?: number;
    length?: number;
    height?: number;
    width?: number;
  }>;
}

interface ProductForPricing {
  offerId?: string | number;
  categoryId?: string;
  hsn?: string;
  hsnTaxPercent?: number;
  topCategoryId?: string | number;
  secondCategoryId?: string | number;
  thirdCategoryId?: string | number;
  productShippingInfo?: any;
}

interface VariantWithPricing {
  skuId: string | number;
  fenxiaoPriceInfo?: {
    onePiecePrice?: string | number;
    offerPrice?: string | number;
  };
  consignPrice?: string | number;
  price?: string | number;
  weight?: number;
  length?: number;
  height?: number;
  width?: number;
  chargeableWeight?: number; // Calculated weight for pricing (includes buffer, max logic)
  ap?: number;
  cp?: number;
  finalPrice?: number;           // Dropshipping final price (from onePiecePrice)
  breakdown?: Record<string, unknown>;
  localShipping?: number;
  ls?: number;
  wholesaleFinalPrice?: number;  // Wholesale final price (from consignPrice)
  wholesaleBreakdown?: Record<string, unknown>;
  skuAttributes?: Array<{
    attributeId: string;
    attributeName: string;
    attributeNameTrans?: string;
    value: string;
    valueTrans?: string;
    skuImageUrl?: string;
  }>;
}

class CentralizePriceCalculation {
  /**
   * Apply markup and calculate FBP for a single variant
   * Calculates both dropshipping price (onePiecePrice) and wholesale price (consignPrice)
   */
  static async applyMarkupToVariantPrices(variant: VariantWithPricing, product: ProductForPricing): Promise<VariantWithPricing> {
    // Get dropshipping price (onePiecePrice - single piece price)
    const dropshippingBasePrice = parseFloat(String(variant.fenxiaoPriceInfo?.onePiecePrice || variant.fenxiaoPriceInfo?.offerPrice || variant.consignPrice || variant.price || 0));
    
    // Get wholesale price (consignPrice or offerPrice - bulk price)
    const wholesaleBasePrice = parseFloat(String(variant.consignPrice || variant.fenxiaoPriceInfo?.offerPrice || variant.price || 0));

    const modifiedVariant: VariantWithPricing = {
      ...variant,
      ap: dropshippingBasePrice, // Dropshipping base price (onePiecePrice)
      cp: wholesaleBasePrice,    // Wholesale base price (consignPrice)
    };

    // Calculate dropshipping FBP (using onePiecePrice)
    const dropshippingVariant = { ...modifiedVariant, ap: dropshippingBasePrice };
    const dropshippingPriceData = await calculateFBP([dropshippingVariant], product.offerId, product.categoryId, product.hsn, product.hsnTaxPercent, null, product);
    
    // Calculate wholesale FBP (using consignPrice) - only if different from dropshipping price
    let wholesalePriceData = null;
    if (wholesaleBasePrice !== dropshippingBasePrice) {
      const wholesaleVariant = { ...modifiedVariant, ap: wholesaleBasePrice };
      wholesalePriceData = await calculateFBP([wholesaleVariant], product.offerId, product.categoryId, product.hsn, product.hsnTaxPercent, null, product);
    }

    // Set dropshipping price as default finalPrice
    modifiedVariant.finalPrice = dropshippingPriceData?.finalPrice;
    modifiedVariant.breakdown = dropshippingPriceData?.breakdown;
    modifiedVariant.localShipping = dropshippingPriceData?.localShipping;
    modifiedVariant.ls = dropshippingPriceData?.ls;
    
    // Set wholesale price separately
    modifiedVariant.wholesaleFinalPrice = wholesalePriceData?.finalPrice || dropshippingPriceData?.finalPrice;
    modifiedVariant.wholesaleBreakdown = wholesalePriceData?.breakdown || dropshippingPriceData?.breakdown;

    return modifiedVariant;
  }

  /**
   * Map Alibaba variation with shipping info and calculate pricing
   * When dimensions or weight are missing/0, use default weight value
   * Compare volumetric weight vs actual weight and use the maximum
   * Apply buffer weight percentage to final weight
   * Keep original values for display, use chargeableWeight for pricing
   */
  static async mapAlibabaVariation(variant: VariantWithPricing, shippingInfo: ShippingInfo, product: ProductForPricing): Promise<VariantWithPricing> {
    // Find matching shipping details by skuId
    const shippingDetail = shippingInfo?.skuShippingDetails?.find(detail => detail.skuId == variant.skuId);

    // Get raw values from shipping details (these are original values for display)
    let rawWeight = shippingDetail?.weight || 0;
    let rawLength = shippingDetail?.length || 0;
    let rawHeight = shippingDetail?.height || 0;
    let rawWidth = shippingDetail?.width || 0;

    // ADDITIVE: parent productShippingInfo fills missing weight and/or incomplete L×W×H (same object as shippingInfo)
    if (shippingInfo && typeof shippingInfo === 'object') {
      const pw = Number(shippingInfo.weight);
      const pl = Number(shippingInfo.length);
      const pwd = Number(shippingInfo.width);
      const ph = Number(shippingInfo.height);
      const parentDimsOk = Number.isFinite(pl) && pl > 0 && Number.isFinite(pwd) && pwd > 0 && Number.isFinite(ph) && ph > 0;
      const dimsOk = rawLength > 0 && rawHeight > 0 && rawWidth > 0;
      if (rawWeight <= 0 && Number.isFinite(pw) && pw > 0) rawWeight = pw;
      if (!dimsOk && parentDimsOk) {
        rawLength = pl;
        rawWidth = pwd;
        rawHeight = ph;
      }
    }

    // Check if dimensions are complete (all non-zero)
    const hasDimensions = rawLength > 0 && rawHeight > 0 && rawWidth > 0;
    const hasWeight = rawWeight > 0;

    // Get default weight and buffer percent from PRICE_LOGIC
    const defaultWeight = PRICE_LOGIC.DEFAULT_DW || 0.5;
    const volumeFactor = PRICE_LOGIC.VOLUME_FACTOR || 5000;
    const weightBufferPercent = PRICE_LOGIC.WEIGHT_BUFFER_PERCENT || 0.05;

    // Calculate volumetric weight if dimensions are available
    const volumetricWeight = hasDimensions ? (rawLength * rawWidth * rawHeight) / volumeFactor : 0;

    // Determine the chargeable weight for pricing:
    // 1. If both dimensions and weight are missing, use default weight (existing logic)
    // 2. If only weight is available (dimensions missing), assign volumetricWeight = defaultWeight, then max(actualWeight, volumetricWeight)
    // 3. If only dimensions are available (weight missing), use max(volumetricWeight, defaultWeight)
    // 4. If both are available, use existing logic (max of actual weight and volumetric weight)
    let chargeableWeight: number;
    let effectiveVolumetricWeight = volumetricWeight;
    
    if (!hasDimensions && !hasWeight) {
      // Both missing - use default weight (existing logic)
      chargeableWeight = defaultWeight;
    } else if (hasWeight && !hasDimensions) {
      // Only weight available (dimensions missing) - assign volumetricWeight = defaultWeight, then compare
      effectiveVolumetricWeight = defaultWeight;
      chargeableWeight = Math.max(rawWeight, effectiveVolumetricWeight);
    } else if (hasDimensions && !hasWeight) {
      // Only dimensions available (weight missing) - use max(volumetricWeight, defaultWeight)
      chargeableWeight = Math.max(volumetricWeight, defaultWeight);
    } else {
      // Both available - use existing logic (max of actual weight and volumetric weight)
      chargeableWeight = Math.max(rawWeight, volumetricWeight);
    }

    // Note: Buffer weight is applied in the backend pricing API, not here
    // to avoid double buffering

    const variantWithShipping: VariantWithPricing = {
      ...variant,
      // Keep original values for display
      weight: rawWeight,
      length: rawLength,
      height: rawHeight,
      width: rawWidth,
      // Add chargeable weight for pricing calculations (buffer applied in backend API)
      chargeableWeight: chargeableWeight,
    };

    return await this.applyMarkupToVariantPrices(variantWithShipping, product);
  }

  /**
   * Map all Alibaba variations with shipping info and calculate pricing
   * Returns mapped variations along with min weight/volume stats
   * Uses default weight when dimensions/weight are missing and compares volumetric vs actual weight
   */
  static async mapAlibabaVariations(
    productSkuInfos: VariantWithPricing[],
    shippingInfo: ShippingInfo,
    product: ProductForPricing
  ): Promise<{
    variations: VariantWithPricing[];
    variantsDetails: Record<string, VariantWithPricing>;
    actualVariants: Record<string, VariantWithPricing>;
    minVariantWeight: number;
    minVariantVolume: number;
  }> {
    const variantsDetails: Record<string, VariantWithPricing> = {};
    const actualVariants: Record<string, VariantWithPricing> = {};
    let minVariantWeight = Infinity;
    let minVariantVolume = Infinity;

    // Get default weight from PRICE_LOGIC
    const defaultWeight = PRICE_LOGIC.DEFAULT_DW || 0.5;

    const variations = Array.isArray(productSkuInfos)
      ? await Promise.all(
          productSkuInfos.map(async variant => {
            const mappedVariant = await this.mapAlibabaVariation(variant, shippingInfo, product);
            variantsDetails[mappedVariant.skuId] = mappedVariant;
            
            // Track min weight - use chargeableWeight for pricing calculations
            const variantWeight = mappedVariant.chargeableWeight || defaultWeight;
            minVariantWeight = Math.min(minVariantWeight, variantWeight);
            
            // Track min volume - only if dimensions are available
            const volume = (mappedVariant.height || 0) * (mappedVariant.width || 0) * (mappedVariant.length || 0);
            if (volume > 0) {
              minVariantVolume = Math.min(minVariantVolume, volume);
            }
            
            actualVariants[variant.skuId] = { ...variant, ...mappedVariant };
            return mappedVariant;
          })
        )
      : [];

    return {
      variations,
      variantsDetails,
      actualVariants,
      minVariantWeight: minVariantWeight === Infinity ? defaultWeight : minVariantWeight,
      minVariantVolume: minVariantVolume === Infinity ? 0 : minVariantVolume,
    };
  }

  static async getCalculatedPriceRangeList(
    offerId: number,
    priceRangeList: PriceRange[] = [],
    categoryId: string = '',
    WholesaleInitialQuantity: number = null,
    minVariantVolume: number = null,
    minVariantWeight: number = null,
    isDropshippingAvailable: boolean = true,
    product: ProductForPricing = null
  ) {
    if (!offerId || !priceRangeList.length) return null;
    const baseRanges: PriceRange[] = priceRangeList;
    const offerIdNum: number = offerId;
    return (
      await Promise.all(
        baseRanges.map(async range => {
          try {
            //will comment out later
            // Only convert startQuantity from 1 to 2 if dropshipping is available
            if (WholesaleInitialQuantity && range.startQuantity === 1 && isDropshippingAvailable) {
              range.startQuantity = WholesaleInitialQuantity;
            }

            // COMMENTED: Freight API call - now using algorithm's local_shipping_rmb instead
            // const lsRes = await ProductsApiService.getLocalShippingCost(offerIdNum, [{ skuId: offerIdNum, quantity: range.startQuantity }]);
            // const ls = lsRes.success ? lsRes.shippingCost : 0;
            // const finalLS = ls / range.startQuantity;
            
            // LS will be calculated from algorithm's local_shipping_rmb field
            const finalLS = 0;
            
            const calc = await ProductsApiService.calculatePricingAlgorithm({
              ap: parseFloat(range.price),
              ls: finalLS,
              hsn: null,
              hsnTaxPercent: PRICE_LOGIC.HSNTaxPercent,
              dw: minVariantWeight || PRICE_LOGIC.DEFAULT_DW,
              vw: (minVariantVolume || 0) / PRICE_LOGIC.VOLUME_FACTOR,
              categoryId: categoryId,
              sku: offerId,
              topCategoryId: product?.topCategoryId || '',
              secondCategoryId: product?.secondCategoryId || '',
              thirdCategoryId: product?.thirdCategoryId || '',
              productShippingInfo: product?.productShippingInfo || {},
            });
            const FBP = calc?.data?.breakdown?.FBP as number;
            const LS = calc?.data?.breakdown?.LS as number;
            return { ...range, finalPrice: FBP, localShipping: LS, ls: finalLS };
          } catch {
            Message().showToast(`Failed to Fetch One of Price Range in Pricing Service.`, ToastTypes.ERROR);
          }
        })
      )
    ).filter(range => !!range.finalPrice);
  }

  // Fetch local shipping cost separately
  static async fetchLocalShippingCost(productOfferId, skuIdsNQuantities = [], message = null) {
    try {
      const result = await ProductsApiService.getLocalShippingCost(productOfferId, skuIdsNQuantities);
      if (result.success) {
        return result.shippingCost;
      } else {
        console.warn(`Failed to fetch local shipping cost: API Response `, result);
        return null;
      }
    } catch (error) {
      if (message) message.showToast(`Failed to fetch local shipping cost: ${error.message}`, ToastTypes.ERROR);
      console.error(`Failed to fetch local shipping cost: API Error `, error);
      return null;
    }
  }

  static isMinMaxRange(productSkuInfos: Partial<Variation>[]) {}

  static async productPricingAlgorithm(product, productSaleInfo, productShippingInfo, productSkuInfos, onlyDS = false) {
    const isDropshipping = product.minOrderQuantity === 1;

    let minRangePrice = 0;
    let maxRangePrice = 0;
    let rangedStartQuantity = 1;

    let minVariantPrice = Infinity;
    let maxVariantPrice = 0;

    let minVariantDeadWeight = Infinity;
    let maxVariantDeadWeight = 0;

    let minVariantVolumetricWeight = Infinity;
    let maxVariantVolumetricWeight = 0;

    let variantSkuId;
    let variantDeadWeight;
    let variantVolumetricWeight;

    const requiredDetails = {
      priceRangeList: productSaleInfo?.priceRangeList,
      variantShippingDetails: productShippingInfo?.skuShippingDetails,
      productShippingInfo: productShippingInfo,
      variants: productSkuInfos || [],
    };
    // removed fallback dimensions n wight given by api, keep only seller given dimensions n weight.
    // if(requiredDetails.productShippingInfo){
    //    const { weight, width, height, length } = requiredDetails.productShippingInfo || {};
    //   variantDeadWeight = minVariantDeadWeight = weight ?? minVariantDeadWeight;
    //   variantVolumetricWeight = minVariantVolumetricWeight = length && width && height ? length * width * height : 0;
    //    maxVariantDeadWeight = weight ?? maxVariantDeadWeight;
    //     maxVariantVolumetricWeight = length && width && height ? length * width * height : 0;
    // }

    if (requiredDetails.priceRangeList?.length > 0) {
      requiredDetails.priceRangeList.sort((a, b) => parseFloat(a.startQuantity) - parseFloat(b.startQuantity));
      rangedStartQuantity = parseInt(requiredDetails.priceRangeList[0].startQuantity);
      minRangePrice = parseFloat(requiredDetails.priceRangeList[0].price);
      maxRangePrice = parseFloat(requiredDetails.priceRangeList[requiredDetails.priceRangeList.length - 1].price);
    }

    const minPriceVariant = requiredDetails.variants.reduce((acc, v) => {
      const accPrice = parseFloat(acc?.fenxiaoPriceInfo?.onePiecePrice || acc?.fenxiaoPriceInfo?.offerPrice || acc?.consignPrice || acc?.price || 0) || Infinity;
      const vPrice = parseFloat(v?.fenxiaoPriceInfo?.onePiecePrice || v?.fenxiaoPriceInfo?.offerPrice || v?.consignPrice || v?.price || 0);
      return accPrice > vPrice ? v : acc;
    }, {});

    // ALWAYS use weight and dimensions from detailed_info.productShippingInfo.skuShippingDetails by matching skuId
    // For minimum AP_RMB calculation, find the variant with minimum price and get its weight/dimensions from skuShippingDetails
    if (requiredDetails?.variantShippingDetails?.length) {
      // First, find the minimum price variant
      const minPriceVariantSkuId = minPriceVariant?.skuId;
      
      // Find shipping details for the minimum price variant by matching skuId
      const minPriceShippingDetail = requiredDetails.variantShippingDetails.find(shp => 
        String(shp.skuId) === String(minPriceVariantSkuId) || shp.skuId === minPriceVariantSkuId
      );

      if (minPriceShippingDetail && minPriceVariant) {
        const skuVariantPrice = parseFloat(
          minPriceVariant?.fenxiaoPriceInfo?.onePiecePrice || minPriceVariant?.fenxiaoPriceInfo?.offerPrice || minPriceVariant?.consignPrice || minPriceVariant?.price || 0
        );
        
        // Use weight and dimensions from skuShippingDetails for minimum AP_RMB calculation
        const currWeight = minPriceShippingDetail.weight || 0;
        const currLength = minPriceShippingDetail.length || 0;
        const currWidth = minPriceShippingDetail.width || 0;
        const currHeight = minPriceShippingDetail.height || 0;

        minVariantPrice = skuVariantPrice;
        variantDeadWeight = currWeight || PRICE_LOGIC.DEFAULT_DW;
        variantVolumetricWeight = (currLength && currWidth && currHeight) 
          ? currLength * currWidth * currHeight 
          : PRICE_LOGIC.DEFAULT_DW;
        variantSkuId = minPriceVariant.skuId;

        // Also track max price variant for completeness
        for (const shp of requiredDetails.variantShippingDetails) {
          const skuVariant = requiredDetails.variants.find(v => 
            String(v.skuId) === String(shp.skuId) || v.skuId === shp.skuId
          );
          if (skuVariant) {
            const skuVariantPrice = parseFloat(
              skuVariant?.fenxiaoPriceInfo?.onePiecePrice || skuVariant?.fenxiaoPriceInfo?.offerPrice || skuVariant?.consignPrice || skuVariant?.price || 0
            );
            if (skuVariantPrice > maxVariantPrice) {
              maxVariantPrice = skuVariantPrice;
            }
          }
        }
      } else {
        // Fallback to original logic if shipping detail not found
        for (const shp of requiredDetails?.variantShippingDetails) {
          const currWeight = shp.weight;
          const currHeight = shp.height;
          const currWidth = shp.width;
          const currLength = shp.length;

          const skuVariant = minPriceVariant.skuId === parseInt(shp.skuId) ? minPriceVariant : null;
          if (skuVariant) {
            const skuVariantPrice = parseFloat(
              skuVariant?.fenxiaoPriceInfo?.onePiecePrice || skuVariant?.fenxiaoPriceInfo?.offerPrice || skuVariant?.consignPrice || skuVariant?.price || 0
            );
            const maxPriceAvailable = skuVariantPrice > maxVariantPrice;
            if (maxPriceAvailable) {
              maxVariantPrice = skuVariantPrice;
              variantDeadWeight = currWeight ? currWeight : PRICE_LOGIC.DEFAULT_DW;
              variantVolumetricWeight = currLength && currWidth && currHeight ? currLength * currWidth * currHeight : PRICE_LOGIC.DEFAULT_DW;
            }
            const minPriceAvailable = skuVariantPrice <= minVariantPrice;
            if (minPriceAvailable) {
              minVariantPrice = skuVariantPrice;
              variantDeadWeight = currWeight;
              if (currLength && currWidth && currHeight) {
                const currentVolumetricWeight = currLength * currWidth * currHeight;
                variantVolumetricWeight = currentVolumetricWeight;
              }
            }

            variantSkuId = skuVariant.skuId;
          }
        }
      }
    }
    if (requiredDetails?.variantShippingDetails?.length === 0 && requiredDetails.variants?.length > 0) {
      requiredDetails.variants.forEach(variant => {
        const skuVariantPrice = parseFloat(variant?.fenxiaoPriceInfo?.onePiecePrice || variant?.consignPrice || variant?.price || variant?.fenxiaoPriceInfo?.offerPrice || 0);
        if (minVariantPrice >= skuVariantPrice) {
          minVariantPrice = skuVariantPrice;
          minVariantDeadWeight = variantDeadWeight = PRICE_LOGIC.DEFAULT_DW;
        }
        if (maxVariantPrice < skuVariantPrice) {
          maxVariantPrice = skuVariantPrice;
          maxVariantDeadWeight = variantVolumetricWeight = PRICE_LOGIC.DEFAULT_DW;
        }
      });
    }

    if (minVariantDeadWeight === Infinity) minVariantDeadWeight = 0;
    if (minVariantVolumetricWeight === Infinity) minVariantVolumetricWeight = 0;
    if (minVariantPrice === Infinity) minVariantPrice = 0;

    const isMinMaxRange = minVariantPrice !== maxVariantPrice;
    const calculationData = {
      ap: minVariantPrice,
      ls: 0,
      hsn: false,
      hsnTaxPercent: PRICE_LOGIC.HSNTaxPercent,
      dw: variantDeadWeight || minVariantDeadWeight || maxVariantDeadWeight || PRICE_LOGIC.DEFAULT_DW,
      vw: (variantVolumetricWeight || minVariantVolumetricWeight || maxVariantVolumetricWeight || PRICE_LOGIC.DEFAULT_DW) / PRICE_LOGIC.VOLUME_FACTOR,
      sku: String(product.offerId), // offerId is typeof number in api response.
    };
    
    // COMMENTED: Freight API call - now using algorithm's local_shipping_rmb instead
    // let localShipping = 0;
    // if (onlyDS || isDropshipping || isMinMaxRange) {
    //   localShipping = await this.fetchLocalShippingCost(product.offerId, [{ skuId: variantSkuId || product.offerId, quantity: 1 }]);
    //   calculationData.ls = localShipping;
    // } else {
    //   localShipping = await this.fetchLocalShippingCost(product.offerId, [{ skuId: variantSkuId || product.offerId, quantity: rangedStartQuantity }]);
    //   calculationData.ls = localShipping / rangedStartQuantity;
    //   calculationData.ap = minRangePrice;
    // }
    
    // LS will be calculated from algorithm's local_shipping_rmb field
    calculationData.ls = 0;
    if (!(onlyDS || isDropshipping || isMinMaxRange)) {
      calculationData.ap = minRangePrice;
    }
    
    const pricingAlgorithm = await ProductsApiService.calculatePricingAlgorithm(calculationData);
    if (pricingAlgorithm) {
      product.priceInfo.finalPrice = pricingAlgorithm.data.breakdown.FBP;
      product.finalPrice = pricingAlgorithm.data.breakdown.FBP;
      product.sp = product.finalPrice;
    }

    return { product, calculationData };
  }

  static async getPricingAlgo() {
    try {
      const result = await ProductsApiService.getPricingAlgorithm();
      return result.data;
    } catch (error) {
      toast.error(error.message);
      return null;
    }
  }

  static async calculateCartProductTotalLMD(products: any[]) {
    // Ensure pricing config is loaded
    const algoData = await CentralizePriceCalculation.getPricingAlgo();
    const formula = algoData?.formula || {};
    
    // Get values from formula or use defaults
    const defaultDW = formula.default_chargeable_weight || PRICE_LOGIC.DEFAULT_DW || 0.5;
    const volumeFactor = PRICE_LOGIC.VOLUME_FACTOR || 5000;
    const weightBufferPercent = formula.weight_buffer_percent || 0.05;
    const gstFreightPercent = formula.gst_freight || 0.18;

    /**
     * Calculate LMD for dropship products based on cumulative weight slabs
     * Formula:
     * - First 500g: ₹100 (base)
     * - Each additional 500g (or part thereof): +₹40
     * 
     * Examples:
     * - 500g: ₹100
     * - 1kg (2 slabs): ₹100 + ₹40 = ₹140
     * - 1.05kg (3 slabs): ₹100 + ₹40 + ₹40 = ₹180
     * - 1.5kg (3 slabs): ₹100 + ₹40 + ₹40 = ₹180
     * - 2kg (4 slabs): ₹100 + ₹40 + ₹40 + ₹40 = ₹220
     */
    const calculateDropshipLMD = (weightInKg: number): number => {
      if (weightInKg <= 0) return 100; // Default to base if weight is 0 or negative
      
      const baseCharge = 100; // ₹100 for first 500g
      const perSlabCharge = 40; // ₹40 for each additional 500g
      const slabSizeKg = 0.5; // 500g in kg
      
      // Calculate number of 500g slabs (rounded up)
      const numSlabs = Math.ceil(weightInKg / slabSizeKg);
      
      // LMD = base ₹100 + (number of additional slabs × ₹40)
      const lmd = baseCharge + ((numSlabs - 1) * perSlabCharge);
      
      return lmd;
    };

    const variantsChargeableWeights: Array<{ 
      chargeableWeight: number; 
      quantity: number; 
      pricingMode: string;
    }> = [];
    
    for (const { variants, categoryId, pricingMode } of products) {
      for (const { skuId, weight, length, width, height, quantity } of variants) {
        const rawWeight = weight || 0;
        const rawLength = length || 0;
        const rawHeight = height || 0;
        const rawWidth = width || 0;

        const hasDimensions = rawLength > 0 && rawHeight > 0 && rawWidth > 0;
        const hasWeight = rawWeight > 0;

        const volumetricWeight = hasDimensions ? (rawLength * rawWidth * rawHeight) / volumeFactor : 0;

        // Calculate chargeable weight per variant using same logic as ProductDetails
        let chargeableWeight: number;
        
        if (!hasDimensions && !hasWeight) {
          // Both missing - use default weight
          chargeableWeight = defaultDW;
        } else if (hasWeight && !hasDimensions) {
          // Only weight available - max(weight, defaultWeight)
          chargeableWeight = Math.max(rawWeight, defaultDW);
        } else if (hasDimensions && !hasWeight) {
          // Only dimensions available - max(volumetricWeight, defaultWeight)
          chargeableWeight = Math.max(volumetricWeight, defaultDW);
        } else {
          // Both available - max(weight, volumetricWeight)
          chargeableWeight = Math.max(rawWeight, volumetricWeight);
        }

        // Apply buffer to chargeable weight (same as wholesale)
        const chargeableWeightWithBuffer = chargeableWeight + (chargeableWeight * weightBufferPercent);

        variantsChargeableWeights.push({
          chargeableWeight: chargeableWeightWithBuffer,
          quantity,
          pricingMode: pricingMode || 'dropshipping',
        });
      }
    }

    // console.log('[LMD] Inputs — formula:', {
    //   defaultDW_kg: defaultDW,
    //   volumeFactor: volumeFactor,
    //   weightBufferPercent: weightBufferPercent,
    //   gstFreightPercent: gstFreightPercent,
    // });
    

    const totalQuantity = variantsChargeableWeights.reduce(
      (sum, variant) => sum + variant.quantity,
      0
    );

    // Separate dropship and wholesale variants
    const dropshipVariants = variantsChargeableWeights.filter(v => v.pricingMode === 'dropshipping');
    const wholesaleVariants = variantsChargeableWeights.filter(v => v.pricingMode !== 'dropshipping');

    let totalLMD = 0;
    
    // Calculate LMD for DROPSHIP products (per variant, then multiply by quantity)
    for (const variant of dropshipVariants) {
      // For dropship: calculate LMD per unit weight, then multiply by quantity
      const lmdPerUnit = calculateDropshipLMD(variant.chargeableWeight);
      const lmdForVariant = lmdPerUnit * variant.quantity;
      totalLMD += lmdForVariant;
      
      console.log('[LMD] Dropship variant:', {
        'weight per unit (kg)': Math.round(variant.chargeableWeight * 1000) / 1000,
        'quantity': variant.quantity,
        'LMD per unit (INR)': lmdPerUnit,
        'LMD total for variant (INR)': lmdForVariant,
      });
    }

    // Calculate LMD for WHOLESALE products (total weight based - original logic)
    if (wholesaleVariants.length > 0) {
      // Sum all wholesale weights first (original behavior)
      const totalWholesaleWeight = wholesaleVariants.reduce(
        (sum, variant) => sum + (variant.chargeableWeight * variant.quantity),
        0
      );
      
      // Apply original wholesale formula to total weight
      const baseWeight = 0.5; // 500g in kg
      const defaultLMD = 60;
      const perSlabCharge = 40;
      const numSlabs = Math.ceil(totalWholesaleWeight / baseWeight);
      const wholesaleLMD = (numSlabs * perSlabCharge) + defaultLMD;
      totalLMD += wholesaleLMD;
      
      // console.log('[LMD] Wholesale (all variants combined):', {
      //   'total weight (kg)': Math.round(totalWholesaleWeight * 1000) / 1000,
      //   'num slabs': numSlabs,
      //   'LMD (INR)': wholesaleLMD,
      // });
    }

    // console.log('[LMD] Total calculation:', {
    //   'total LMD (INR)': Math.round(totalLMD * 100) / 100,
    // });

    // Calculate GST on LMD shipping
    const gstOnLMD = totalLMD * gstFreightPercent;
    const totalLMDWithGST = totalLMD + gstOnLMD;

    const gstKey = 'GST on LMD (' + (gstFreightPercent * 100) + '%) INR';
    // console.log('[LMD] Final amounts:', {
    //   'total LMD (base) INR': Math.round(totalLMD * 100) / 100,
    //   [gstKey]: Math.round(gstOnLMD * 100) / 100,
    //   'total LMD with GST INR': Math.round(totalLMDWithGST * 100) / 100,
    // });

    const LMD_Details = {
      totalVW: 0, // in KG. (kept for backward compatibility, but not used)
      totalDW: 0, // in KG. (kept for backward compatibility, but not used)
      totalQuantity: totalQuantity,
      totalLMD: totalLMD, // in INR.
      gstOnLMD: gstOnLMD, // GST on LMD shipping
      totalLMDWithGST: totalLMDWithGST, // Total LMD including GST
    };

    return LMD_Details;
  }
}

export default CentralizePriceCalculation;
